/** Client-side diagram recovery when analysis artifacts have empty node lists. */

const INTEGRATION_FAMILIES = [
  ['idoc', 'IDoc', 'async'],
  ['rfc_bapi', 'RFC / BAPI', 'sync'],
  ['web_services', 'Web services', 'sync'],
  ['bw_extractors', 'BW extractors', 'batch'],
  ['odata', 'OData', 'sync'],
  ['slt', 'SLT', 'realtime'],
  ['flat_file', 'Flat file', 'batch'],
];

const ROLE_TO_LAYER = {
  saas: 'saas',
  onprem: 'onprem',
  desktop: 'desktop',
  middleware: 'integration',
  integration_partner: 'external',
  analytics: 'external',
  erp: 'onprem',
  database: 'infra',
  operations: 'external',
};

function slugId(name) {
  const slug = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 48);
  return slug || 'system';
}

function asInt(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function ifaceTotals(family) {
  if (!family || typeof family !== 'object') return { total: null, risk: 0 };
  const total = asInt(family.total);
  const risk = (asInt(family.unavailable) || 0) + (asInt(family.blocked) || 0);
  return { total, risk };
}

function ifaceLabel(key, family) {
  const { total, risk } = ifaceTotals(family);
  const bits = [key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())];
  if (total != null) bits.push(String(total));
  if (risk) bits.push(`${risk} at risk`);
  const notes = String(family?.notes || '').trim();
  if (notes) bits.push(notes.slice(0, 40));
  return bits.slice(0, 3).join(' · ');
}

export function unwrapArtifactContent(content) {
  if (!content) return {};
  if (typeof content === 'string') {
    try {
      return JSON.parse(content);
    } catch {
      return {};
    }
  }
  if (typeof content !== 'object') return {};
  if (Array.isArray(content.nodes) || content.markdown != null) return content;
  if (content.value && typeof content.value === 'object') return content.value;
  if (typeof content.value === 'string') {
    try {
      return JSON.parse(content.value);
    } catch {
      return content;
    }
  }
  return content;
}

export function diagramNodeCount(diagram) {
  const nodes = diagram?.nodes;
  return Array.isArray(nodes) ? nodes.length : 0;
}

function synthesizeLandscape(inventory) {
  const sid = String(inventory?.sid || 'Source ERP').trim();
  const target = String(inventory?.target_s4_version || 'S/4HANA').trim();
  const sourceVer = String(inventory?.source_product_version || '').trim();
  const db = String(inventory?.current_database || '').trim();
  const osName = String(inventory?.operating_system || '').trim();
  const integ = inventory?.integration && typeof inventory.integration === 'object'
    ? inventory.integration
    : {};

  const nodes = [];
  const edges = [];
  const nodeIds = new Set();

  const addNode = (node) => {
    const id = String(node.id || '').trim();
    if (!id || nodeIds.has(id)) return;
    nodeIds.add(id);
    nodes.push(node);
  };

  const coreSub = [sid, sourceVer].filter(Boolean).join(' · ');
  addNode({
    id: 'core_erp',
    label: sid.slice(0, 28) || 'Source ERP',
    subtitle: coreSub.slice(0, 48),
    type: 'sap',
    layer: 'application',
    source: 'Readiness check system identity',
    metrics: [],
  });
  addNode({
    id: 'target_s4',
    label: 'Target S/4HANA',
    subtitle: target.slice(0, 48),
    type: 's4',
    layer: 'application',
    source: 'Readiness check target version',
  });
  edges.push({ from: 'core_erp', to: 'target_s4', label: 'Conversion', kind: 'depends_on' });

  if (db) {
    addNode({
      id: 'database',
      label: db.slice(0, 28),
      subtitle: 'Database',
      type: 'db',
      layer: 'infra',
      source: 'Readiness check technical properties',
    });
    edges.push({ from: 'database', to: 'core_erp', label: 'hosts', kind: 'hosts' });
  }
  if (osName) {
    addNode({
      id: 'operating_system',
      label: osName.slice(0, 28),
      subtitle: 'OS',
      type: 'os',
      layer: 'platform',
      source: 'Readiness check technical properties',
    });
    edges.push({ from: 'operating_system', to: 'core_erp', label: 'runs on', kind: 'hosts' });
  }

  for (const ext of inventory?.external_systems || []) {
    if (!ext || typeof ext !== 'object') continue;
    const name = String(ext.name || '').trim();
    if (!name) continue;
    const eid = String(ext.id || slugId(name)).trim() || slugId(name);
    const role = String(ext.role || 'other').trim().toLowerCase();
    const layer = ROLE_TO_LAYER[role] || 'external';
    let typ = role === 'middleware' ? 'middleware' : 'external';
    if (role === 'erp' || role === 'onprem') typ = 'sap';
    addNode({
      id: eid,
      label: name.slice(0, 28),
      subtitle: String(ext.category || role).trim().slice(0, 48),
      type: typ,
      layer,
      system_id: eid,
      description: String(ext.description || '').trim(),
      source: String(ext.identification_source || 'Readiness check integration extract'),
      metrics: [],
    });
    edges.push({ from: eid, to: 'core_erp', label: 'integrates', kind: 'integrates' });
  }

  for (const [key, title] of INTEGRATION_FAMILIES) {
    const family = integ[key] && typeof integ[key] === 'object' ? integ[key] : {};
    const { total, risk } = ifaceTotals(family);
    if (total == null && !risk) continue;
    const hubId = `${key}_hub`;
    const metrics = [];
    if (total != null) metrics.push(`${total} total`);
    if (risk) metrics.push(`${risk} at risk`);
    addNode({
      id: hubId,
      label: title.slice(0, 28),
      subtitle: ifaceLabel(key, family),
      type: 'integration',
      layer: 'integration',
      source: `Readiness check Interface Impact Analysis (${key})`,
      metrics,
    });
    edges.push({
      from: hubId,
      to: 'core_erp',
      label: ifaceLabel(key, family),
      kind: 'integrates',
      style: risk && total && risk >= total ? 'dashed' : 'solid',
    });
  }

  return {
    title: 'Connected SAP landscape',
    caption: 'Recovered from readiness inventory (analysis output had no diagram nodes)',
    layout: 'layered',
    nodes,
    edges,
  };
}

function synthesizeDataflow(inventory) {
  const sid = String(inventory?.sid || 'Core ERP').trim();
  const integ = inventory?.integration && typeof inventory.integration === 'object'
    ? inventory.integration
    : {};

  const nodes = [];
  const edges = [];
  const nodeIds = new Set();

  const addNode = (node) => {
    const id = String(node.id || '').trim();
    if (!id || nodeIds.has(id)) return;
    nodeIds.add(id);
    nodes.push(node);
  };

  addNode({
    id: 'core_erp',
    label: sid.slice(0, 28) || 'Core ERP',
    subtitle: 'Conversion scope',
    type: 'system',
    layer: 'core',
    source: 'Readiness check system identity',
  });

  for (const ext of inventory?.external_systems || []) {
    if (!ext || typeof ext !== 'object') continue;
    const name = String(ext.name || '').trim();
    if (!name) continue;
    const eid = String(ext.id || slugId(name)).trim() || slugId(name);
    const role = String(ext.role || 'other').trim().toLowerCase();
    const layer = role === 'analytics' || role === 'saas' ? 'target' : 'source';
    addNode({
      id: eid,
      label: name.slice(0, 28),
      subtitle: String(ext.category || '').trim().slice(0, 48),
      type: 'system',
      layer,
      system_id: eid,
      description: String(ext.description || '').trim(),
      source: String(ext.identification_source || 'Readiness check integration extract'),
    });
    if (layer === 'source') {
      edges.push({ from: eid, to: 'core_erp', label: 'inbound', kind: 'inbound' });
    } else {
      edges.push({ from: 'core_erp', to: eid, label: 'outbound', kind: 'outbound' });
    }
  }

  for (const [key, title, flowKind] of INTEGRATION_FAMILIES) {
    const family = integ[key] && typeof integ[key] === 'object' ? integ[key] : {};
    const { total, risk } = ifaceTotals(family);
    if (total == null && !risk) continue;
    const ifaceId = `iface_${key}`;
    addNode({
      id: ifaceId,
      label: title.slice(0, 28),
      subtitle: ifaceLabel(key, family),
      type: 'interface',
      layer: 'interface',
      source: `Readiness check Interface Impact Analysis (${key})`,
      metrics: total != null ? [`${total} total`] : [],
    });
    edges.push({
      from: ifaceId,
      to: 'core_erp',
      label: ifaceLabel(key, family),
      kind: flowKind,
    });
  }

  return {
    title: 'Integration & data flows',
    caption: 'Recovered hub-and-spoke view from readiness integration volumes',
    layout: 'flow',
    nodes,
    edges,
  };
}

/** Fill empty diagram payloads using inventory when RC data exists. */
export function ensureDiagramFromInventory(diagram, inventory, kind) {
  const base = diagram && typeof diagram === 'object' ? diagram : {};
  if (diagramNodeCount(base) > 0) return base;
  if (!inventory || typeof inventory !== 'object' || !Object.keys(inventory).length) {
    return base;
  }
  if (kind === 'landscape') return synthesizeLandscape(inventory);
  if (kind === 'dataflow') return synthesizeDataflow(inventory);
  return base;
}
